import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/context';
import TermsAndConditionsFirstStep from '../../components/TermsAndConditions/FirstdStep';
import TermsAndConditionsSecondStep from '../../components/TermsAndConditions/SecondStep';
import { AuthsService } from '../../services/auths.service';
import { memorizedRefreshToken } from '../../services/refreshToken';
import { toastVariant } from '../../components/ToastVariant/toastVariant';
import { OrderContext } from '../../context/OrderContext/order.context';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';

interface GuardProps {
	children: ReactElement<any, any>;
	shouldHaveAddress?: boolean;
}

const STORE_ROOT_PATH = '/store';
const STORE_LOCATION_PATH = '/store/location';

const BuyerGuardedRoute: React.FC<GuardProps> = ({
	children,
	shouldHaveAddress = false,
}) => {
	const { t } = useTranslate();
	const { order } = useContext(OrderContext);
	const { user } = useContext(AuthContext);
	const location = useLocation();

	const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
	const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
	const [redirecting, setRedirecting] = useState<boolean>(false);

	useEffect(() => {
		if (!user?.role || !user?.company?.industries?.includes('customer')) {
			setRedirecting(true);
			window.location.replace(`${process.env.REACT_APP_LANDING_URL}/en/login`);
		}
	}, [user]);

	useEffect(() => {
		const acceptTerms = localStorage.getItem('terms_and_conditions') === 'true';
		setAcceptedTerms(!acceptTerms);
	}, []);

	const nextStep = () => {
		setAcceptedTerms(false);
		setAcceptTerms(true);
	};

	const finish = async () => {
		try {
			await AuthsService.acceptTermsAndConditions();
			await memorizedRefreshToken();
			localStorage.setItem('terms_and_conditions', 'true');
			setAcceptTerms(false);
		} catch (e) {
			console.log(e);
			toastVariant(
				t(
					'pleaseTryAgainLater',
					'Something went wrong, please try again later',
				),
				true,
			);
		}
	};

	if (redirecting) return null;

	const { pathname } = location;
	const isStoreRoute = pathname.startsWith(STORE_ROOT_PATH);

	const hasContractId = Boolean(order?.contractData?.id);

	const isStoreLocationRoute =
		pathname === STORE_LOCATION_PATH ||
		pathname.startsWith(`${STORE_LOCATION_PATH}/`);

	if (isStoreLocationRoute && !hasContractId) {
		return <Navigate to={STORE_ROOT_PATH} replace state={{ from: location }} />;
	}

	const isAllowedWithoutLocation =
		pathname === STORE_ROOT_PATH ||
		pathname === `${STORE_ROOT_PATH}/` ||
		pathname === STORE_LOCATION_PATH;

	const hasLocation = Boolean(order?.location);

	if (isStoreRoute && !isAllowedWithoutLocation && !hasLocation) {
		return <Navigate to={STORE_ROOT_PATH} replace state={{ from: location }} />;
	}

	if (!isStoreRoute && shouldHaveAddress && !hasLocation) {
		return <Navigate to={STORE_ROOT_PATH} replace state={{ from: location }} />;
	}

	return (
		<>
			{children}
			{acceptedTerms ? (
				<TermsAndConditionsFirstStep
					isOpen={acceptedTerms}
					nextStep={nextStep}
				/>
			) : null}
			{acceptTerms ? (
				<TermsAndConditionsSecondStep
					isOpen={acceptTerms}
					finish={finish}
					target="customer"
				/>
			) : null}
		</>
	);
};

export default BuyerGuardedRoute;
